/**
 * @module components/ACS/ACSAvatar
 * @description Avatar animado del asistente ACS con estética cyberpunk/HUD.
 *
 * Componente puramente visual que representa al asistente IA durante la sesión.
 * Animaciones CSS puras (sin dependencias externas):
 * - Visor horizontal tipo casco cyberpunk en lugar de ojos
 * - Barras EQ animadas cuando el asistente está "hablando" (`isSpeaking`)
 * - Línea de escaneo animada sobre el visor durante la síntesis de respuesta
 * - Brackets HUD en las esquinas
 * - Color de acento configurable por prop (identidad visual de la persona ACS)
 *
 * Diseño:
 * - Fondo oscuro (#0a0d14) con gradiente radial
 * - Visor horizontal (estilo casco cyberpunk) en lugar de ojos
 * - Barras EQ animadas para voz
 * - Brackets HUD en esquinas
 * - Línea de escaneo sobre el visor cuando habla
 * - Color de acento = color de la persona ACS
 *
 * Sin dependencias externas — solo React + SVG.
 */
import React, { useState, useEffect, useRef } from 'react';

interface ACSAvatarProps {
  isSpeaking: boolean;
  color:      string;   // hex accent color de la persona ACS
  initials:   string;   // 2-4 chars para status display
  size?:      number;   // px, default 80
}

// Lookup table de alturas EQ: 8 frames × 7 barras (determinista, sin Math.random)
const EQ_FRAMES: readonly number[][] = [
  [2, 7, 5, 10, 4,  8, 3],
  [5, 9, 3,  7, 8,  4, 6],
  [8, 4, 9,  3, 6,  7, 5],
  [6, 8, 7,  5, 3, 10, 4],
  [4, 3, 8,  6, 9,  5, 7],
  [9, 6, 4, 10, 5,  3, 8],
  [7, 5, 6,  4, 7,  8, 3],
  [3, 8, 5,  9, 4,  6, 7],
];

export function ACSAvatar({ isSpeaking, color, initials, size = 80 }: ACSAvatarProps) {
  const [frame, setFrame] = useState(0);
  const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isSpeaking) {
      frameRef.current = setInterval(() => setFrame(f => (f + 1) % EQ_FRAMES.length), 110);
    } else {
      if (frameRef.current) { clearInterval(frameRef.current); frameRef.current = null; }
      setFrame(0);
    }
    return () => { if (frameRef.current) clearInterval(frameRef.current); };
  }, [isSpeaking]);

  // Barra EQ activa o en reposo
  const eqBars = isSpeaking ? EQ_FRAMES[frame] : [2, 2, 2, 2, 2, 2, 2];

  // Línea de escaneo: barre el visor (y 37→48) usando el frame actual
  const scanY = isSpeaking ? 37 + ((frame * 1.5) % 11) : 37;

  // ID único por instancia para filtros SVG (evita colisiones si hay múltiples avatares)
  const uid = `acs-av-${initials.replace(/[^a-z0-9]/gi, '').slice(0, 6) || 'x'}`;

  // Label de estado
  const statusLabel = isSpeaking
    ? '▶ PROC'
    : `◆ ${initials.slice(0, 4).toUpperCase()}`;

  return (
    <div
      style={{ width: size, height: size, flexShrink: 0 }}
      title="Asistente ACS"
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        overflow="visible"
      >
        <defs>
          {/* Glow suave para elementos activos */}
          <filter id={`${uid}-glow`} x="-25%" y="-25%" width="150%" height="150%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Glow fuerte para visor cuando habla */}
          <filter id={`${uid}-glow-strong`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradiente radial para fondo oscuro */}
          <radialGradient id={`${uid}-bg`} cx="50%" cy="45%" r="55%">
            <stop offset="0%"   stopColor="#1e2a3d" />
            <stop offset="100%" stopColor="#080b12" />
          </radialGradient>

          {/* Gradiente horizontal del visor */}
          <linearGradient id={`${uid}-visor`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={color} stopOpacity="0.05" />
            <stop offset="25%"  stopColor={color} stopOpacity="0.85" />
            <stop offset="50%"  stopColor={color} stopOpacity="1"    />
            <stop offset="75%"  stopColor={color} stopOpacity="0.85" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>

          {/* Gradiente vertical del visor (profundidad) */}
          <linearGradient id={`${uid}-visor-v`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%"   stopColor="white" stopOpacity="0.25" />
            <stop offset="100%" stopColor="black" stopOpacity="0.35" />
          </linearGradient>

          {/* Clip circular para no salirse del avatar */}
          <clipPath id={`${uid}-clip`}>
            <circle cx="50" cy="50" r="46" />
          </clipPath>
        </defs>

        {/* ── Capa 0: glow exterior cuando habla ── */}
        {isSpeaking && (
          <circle
            cx="50" cy="50" r="49"
            fill="none"
            stroke={color}
            strokeWidth="4"
            opacity="0.18"
            filter={`url(#${uid}-glow-strong)`}
          />
        )}

        {/* ── Capa 1: fondo principal ── */}
        <circle cx="50" cy="50" r="46" fill={`url(#${uid}-bg)`} />

        {/* ── Capa 2: contenido (recortado al círculo) ── */}
        <g clipPath={`url(#${uid}-clip)`}>

          {/* Grid de fondo (sutil, estilo terminal) */}
          <g stroke={color} strokeWidth="0.3" opacity="0.07">
            {[20, 30, 40, 50, 60, 70, 80].map(x => (
              <line key={`v${x}`} x1={x} y1="4" x2={x} y2="96" />
            ))}
            {[20, 30, 40, 50, 60, 70, 80].map(y => (
              <line key={`h${y}`} x1="4" y1={y} x2="96" y2={y} />
            ))}
          </g>

          {/* ── Decoración superior ── */}
          <line x1="28" y1="26" x2="72" y2="26" stroke={color} strokeWidth="0.8" opacity="0.45" />
          <circle cx="28" cy="26" r="1.2" fill={color} opacity="0.7" />
          <circle cx="72" cy="26" r="1.2" fill={color} opacity="0.7" />
          {/* Pequeñas marcas en la línea */}
          <line x1="38" y1="24" x2="38" y2="28" stroke={color} strokeWidth="0.8" opacity="0.35" />
          <line x1="50" y1="23" x2="50" y2="29" stroke={color} strokeWidth="0.8" opacity="0.55" />
          <line x1="62" y1="24" x2="62" y2="28" stroke={color} strokeWidth="0.8" opacity="0.35" />

          {/* ── Visor principal ── */}
          {/* Sombra/depth del visor */}
          <rect x="18" y="36.5" width="64" height="12" rx="2.5" fill="black" opacity="0.6" />
          {/* Visor con gradiente de color */}
          <rect
            x="18" y="36" width="64" height="11" rx="2"
            fill={`url(#${uid}-visor)`}
            filter={isSpeaking ? `url(#${uid}-glow-strong)` : `url(#${uid}-glow)`}
          />
          {/* Reflejo superior del visor */}
          <rect x="19" y="36.5" width="62" height="4" rx="1.5"
            fill={`url(#${uid}-visor-v)`}
            opacity="0.5"
          />
          {/* Centro brillante */}
          <circle
            cx="50" cy="41.5" r={isSpeaking ? 2 : 1.5}
            fill="white"
            opacity={isSpeaking ? 0.95 : 0.5}
          />

          {/* ── Línea de escaneo (solo cuando habla) ── */}
          {isSpeaking && (
            <line
              x1="19" y1={scanY} x2="81" y2={scanY}
              stroke="white" strokeWidth="0.9" opacity="0.55"
            />
          )}

          {/* ── Barras EQ (voz) ── */}
          {eqBars.map((h, i) => {
            const barW   = 5.2;
            const gap    = 2.4;
            const totalW = eqBars.length * barW + (eqBars.length - 1) * gap;
            const startX = 50 - totalW / 2;
            const x      = startX + i * (barW + gap);
            const barH   = Math.max(2, Math.min(h, 11));
            const baseY  = 68;
            return (
              <rect
                key={i}
                x={x} y={baseY - barH}
                width={barW} height={barH}
                rx="1"
                fill={color}
                opacity={isSpeaking ? (0.5 + (barH / 22)) : 0.25}
                filter={isSpeaking && barH > 7 ? `url(#${uid}-glow)` : undefined}
              />
            );
          })}

          {/* ── Decoración inferior ── */}
          <line x1="28" y1="75" x2="72" y2="75" stroke={color} strokeWidth="0.8" opacity="0.35" />

          {/* ── Texto de estado ── */}
          <text
            x="50" y="84"
            textAnchor="middle"
            fontSize="5.5"
            fill={color}
            opacity={isSpeaking ? 0.9 : 0.45}
            fontFamily="'Courier New', Courier, monospace"
            letterSpacing="2.5"
            style={{ transition: 'opacity 0.4s ease' }}
          >
            {statusLabel}
          </text>
        </g>

        {/* ── Capa 3: anillo exterior ── */}
        <circle
          cx="50" cy="50" r="46"
          fill="none"
          stroke={color}
          strokeWidth={isSpeaking ? 1.8 : 1.2}
          opacity={isSpeaking ? 0.85 : 0.45}
          filter={isSpeaking ? `url(#${uid}-glow)` : undefined}
          style={{ transition: 'all 0.4s ease' }}
        />

        {/* Anillo interior sutil */}
        <circle cx="50" cy="50" r="43" fill="none" stroke={color} strokeWidth="0.4" opacity="0.2" />

        {/* ── HUD brackets en esquinas ── */}
        {/* Top-left */}
        <path d="M17,27 L17,18 L26,18"
          stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="square"
          opacity={isSpeaking ? 0.85 : 0.55}
          filter={isSpeaking ? `url(#${uid}-glow)` : undefined}
        />
        {/* Top-right */}
        <path d="M74,18 L83,18 L83,27"
          stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="square"
          opacity={isSpeaking ? 0.85 : 0.55}
          filter={isSpeaking ? `url(#${uid}-glow)` : undefined}
        />
        {/* Bottom-left */}
        <path d="M17,73 L17,82 L26,82"
          stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="square"
          opacity={isSpeaking ? 0.85 : 0.55}
          filter={isSpeaking ? `url(#${uid}-glow)` : undefined}
        />
        {/* Bottom-right */}
        <path d="M74,82 L83,82 L83,73"
          stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="square"
          opacity={isSpeaking ? 0.85 : 0.55}
          filter={isSpeaking ? `url(#${uid}-glow)` : undefined}
        />
      </svg>
    </div>
  );
}
